import React, {
  useCallback, useEffect, useState,
} from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box, Button, Chip, Divider, Paper, Tab, Tabs, Typography,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';

import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import ApiClient from '../../loopback/apiClient';
import {
  AggregatedSubmissionData,
  DigitalCase,
  DigitalQuestion,
  DigitalSurvey,
  QuestionTypeV2,
  RootState,
} from '../../redux/types';
import {
  resetDigitalSurvey,
  fetchFullDigitalSurvey,
  setSelectedDigitalsection,
  updateDigitalSection,
  updateDigitalSurvey,
  setSelectedCase,
  addDigitalCase,
  setDigitalCases,
  updateDigitalCase,
  deleteDigitalCase,
  addDigitalQuestion,
  updateDigitalQuestion,
  deleteDigitalQuestion,
  setDigitalQuestions,
  fetchDigitalGroundTruths,
  addOrUpdateDigitalGroundTruth,
  deleteDigitalGroundTruth,
  showNotification,
  showErrorNotification,
  addCase,
  createSharedLink,
  getSharedLink,
  deleteSharedLink,
  addQuestionCaseSetting,
  updateQuestionCaseSetting,
  bulkSettingsAllCasesPerQuestion,
  bulkSettingsAllQuestionsPerCase,
  fetchDigitalGroundTruthGroups,
  addDigitalGroundTruthGroup,
  setSelectedDigitalGroundTruthGroup,
  deleteDigitalGroundTruthGroup,
  fetchDigitalSectionScores,
  fetchDigitalSurveyScores,
  clearDigitalSectionScores,
  clearDigitalSurveyScores,
  addOrUpdateDigitalSectionScore,
  addOrUpdateDigitalSurveyScore,
  fetchDigitalQuestionScores,
  clearDigitalQuestionScores,
  addOrUpdateDigitalQuestionScore,
  setLoading,
} from '../../redux/actions';
import {
  selectDigitalSections,
  selectDigitalCases,
  selectDigitalQuestionsBySection,
  selectSelectedSection,
  selectSelectedCase,
  selectDigitalGTDictionaryByQuestionAndCase,
  selectContextQuestionBySection,
  selectSettingsDictionaryByQuestionAndCase,
  selectContextQuestionSettingsByQuestionId,
  selectDigitalGTGroups,
  selectSelectedGroundTruthGroup,
  selectGroundTruthsPerSectionAndGroup,
  selectGroundTruthsPerSection,
  selectDigitalSectionScores,
  selectDigitalSurveyScores,
  selectDigitalQuestionScores,
} from '../../redux/reducers/SurveyDataReducer';
import QuestionEditor from '../../components/questions/QuestionEditor';
import CaseEditor from '../../components/survey/editor/CaseEditor';
import DigitalGroundTruthEditor from '../../components/digitalTrial/DigitalGroundTruthEditor';
import HeadingTitle from '../../components/layout/HeadingTitle';
import SubHeadingTitle from '../../components/layout/SubHeadingTitle';
import Prompt from '../../components/layout/Prompt';
import ScoreEditor from '../../components/survey/editor/ScoreEditor';
import { getBaseUrl } from '../../utilities/urlHelper';
import QuestionCaseSettingsGrid from '../../components/survey/editor/QuestionCaseSettingsGrid';
import ContextQuestionSettings from '../../components/survey/editor/ContextQuestionSettings';
import GroundTruthToolbar from '../../components/groundtruth/GroundTruthToolbar';
import QuestionScoreEditor from '../../components/survey/editor/QuestionScoreEditor';
import FaerbungReviewSettings from '../../components/survey/editor/FaerbungReviewSettings';
import useGetTrialById from '../../hooks/trial/useGetTrialById';
import SectionSettings from '../../components/survey/editor/SectionSettings';
import AggregatedTabPanel from './AggregatedTabPanel';
import SurveyOptionContext from './SurveyOptionContext';
import { selectIsEditorPrivileged } from '../../redux/reducers/Users';
import SectionReferenceLinksEditor from '../../components/survey/editor/SectionReferenceLinksEditor';
import ReferenceLinks from '../../components/survey/questionnaire/ReferenceLinks';

/**
 * TabPanel component
 */
type TabPanelProps = {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`survey-editor-tabpanel-${index}`}
      aria-labelledby={`survey-editor-tab-${index}`}
    >
      {
        children
      }
    </div>
  );
}

type MatchParams = {
  trialId: string;
};

type Props = PropsFromRedux;

const SurveyEditor: React.FC<Props> = ({
  isEditorPrivileged,
  survey,
  sections,
  selectedSection,
  cases,
  questions,
  contextQuestions,
  questionsById,
  gtDictionaryByQuestionAndCase,
  settingsByQuestionAndCase,
  sharedLink,
  fetchingSharedLink,
  contextQuestionSettingByQuestionId,
  groundTruthGroups,
  selectedGroundTruthGroup,
  groundTruthsPerSectionAndGroup,
  groundTruthsPerSection,
  digitalSectionScores,
  digitalSurveyScores,
  digitalQuestionScores,
  settingsDictionary,
  sectionsById,
  resetDigitalSurvey: resetDigitalSurveyAction,
  fetchFullDigitalSurvey: fetchFullDigitalSurveyAction,
  setSelectedDigitalsection: setSelectedDigitalsectionAction,
  updateDigitalSection: updateDigitalSectionAction,
  setSelectedCase: setSelectedCaseAction,
  addDigitalCase: addDigitalCaseAction,
  addCase: addCaseReduxAction,
  setDigitalCases: setDigitalCasesAction,
  updateDigitalCase: updateDigitalCaseAction,
  deleteDigitalCase: deleteDigitalCaseAction,
  addDigitalQuestion: addDigitalQuestionAction,
  updateDigitalQuestion: updateDigitalQuestionAction,
  deleteDigitalQuestion: deleteDigitalQuestionAction,
  setDigitalQuestions: setDigitalQuestionsAction,
  fetchDigitalGroundTruths: fetchDigitalGroundTruthsAction,
  fetchDigitalGroundTruthGroups: fetchDigitalGroundTruthGroupsAction,
  addOrUpdateDigitalGroundTruth: addOrUpdateDigitalGroundTruthAction,
  deleteDigitalGroundTruth: deleteDigitalGroundTruthAction,
  showErrorNotification: showErrorNotificationAction,
  showNotification: showNotificationAction,
  createSharedLink: createSharedLinkAction,
  getSharedLink: getSharedLinkAction,
  deleteSharedLink: deleteSharedLinkAction,
  addQuestionCaseSetting: addQuestionCaseSettingAction,
  updateQuestionCaseSetting: updateQuestionCaseSettingAction,
  bulkSettingsAllCasesPerQuestion: bulkSettingsAllCasesPerQuestionAction,
  bulkSettingsAllQuestionsPerCase: bulkSettingsAllQuestionsPerCaseAction,
  addDigitalGroundTruthGroup: addDigitalGroundTruthGroupAction,
  setSelectedDigitalGroundTruthGroup: setSelectedDigitalGroundTruthGroupAction,
  deleteDigitalGroundTruthGroup: deleteDigitalGroundTruthGroupAction,
  fetchDigitalSectionScores: fetchDigitalSectionScoresAction,
  clearDigitalSectionScores: clearSectionScoresAction,
  addOrUpdateDigitalSectionScore: addOrUpdateDigitalSectionScoreAction,
  fetchDigitalSurveyScores: fetchDigitalSurveyScoresAction,
  clearDigitalSurveyScores: clearSurveyScoresAction,
  addOrUpdateDigitalSurveyScore: addOrUpdateDigitalSurveyScoreAction,
  fetchDigitalQuestionScores: fetchDigitalQuestionScoresAction,
  clearDigitalQuestionScores: clearQuestionScoresAction,
  addOrUpdateDigitalQuestionScore: addOrUpdateDigitalQuestionScoreAction,
  setLoading: setLoadingAction,
}: PropsFromRedux) => {
  const params = useParams<MatchParams>();
  const { trialId } = params;
  const [warningTitle, setWarningTitle] = useState<string>('');
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [aggregatedSubmission, setAggregatedSubmission] = useState<AggregatedSubmissionData>();
  const [tabValue, setTabValue] = useState<number>(0);
  const [
    selectedModel,
    setSelectedModel,
  ] = useState<{
    id: number, callback: typeof deleteDigitalCaseAction | typeof deleteDigitalQuestionAction
  }>();
  const trial = useGetTrialById(Number(trialId));
  const [disableEdit, setDisableEdit] = useState<boolean>(false);
  const [trialHasStarted, setTrialHasStarted] = useState<boolean>(false);

  useEffect(() => {
    const trialStarted = moment().utc().isSameOrAfter(trial?.startDate);
    // no restriction for admin no 1
    const disableEditSurvey = !isEditorPrivileged && trialStarted;
    setDisableEdit(disableEditSurvey as boolean);
    setTrialHasStarted(trialStarted);
  }, [trial, isEditorPrivileged]);

  useEffect(() => {
    ApiClient.getDigitalSurveyByTrialId(Number(trialId))
      .then((digitalSurveys) => digitalSurveys.filter((ds) => (!ds.exercise)))
      .then((selectedSurvey) => {
        let srvy: DigitalSurvey | Promise<DigitalSurvey>;
        [srvy] = selectedSurvey;
        if (!srvy) {
          srvy = ApiClient.createDigitalSurvey(
            { name: '', trialId: Number(trialId), passScore: 0 },
          ).then((newSurvey) => {
            // automatically add a ground truth group with index 1 for new digitalSurvey
            addDigitalGroundTruthGroupAction({ digitalSurveyId: newSurvey.id, index: 1 });
            return newSurvey;
          });
        }
        return srvy;
      })
      .then((dgtlSurvey) => {
        fetchFullDigitalSurveyAction(dgtlSurvey.id);
      });
    return () => {
      // clear digital Survey
      resetDigitalSurveyAction();
    };
  }, [
    trialId,
    resetDigitalSurveyAction,
    fetchFullDigitalSurveyAction,
    addDigitalGroundTruthGroupAction,
  ]);

  useEffect(() => {
    if (trialId && selectedGroundTruthGroup?.id) {
      ApiClient.getAggregatedSubmissionsLive(Number(trialId), Number(selectedGroundTruthGroup.id))
        .then((aggSub) => setAggregatedSubmission(aggSub));
    }
  }, [selectedGroundTruthGroup, trialId]);

  useEffect(() => {
    if (survey?.id) {
      fetchDigitalGroundTruthsAction(survey.id);
      fetchDigitalGroundTruthGroupsAction(survey.id);
      fetchDigitalSectionScoresAction(survey.id);
      fetchDigitalSurveyScoresAction(survey.id);
      fetchDigitalQuestionScoresAction(survey.id);
    }
    return () => {
      // clearing scores is important based on loading scores conditions in GroundTruthToolbar
      clearSectionScoresAction();
      clearSurveyScoresAction();
      clearQuestionScoresAction();
    };
  }, [
    survey?.id,
    fetchDigitalGroundTruthsAction,
    fetchDigitalGroundTruthGroupsAction,
    fetchDigitalSectionScoresAction,
    fetchDigitalSurveyScoresAction,
    clearSectionScoresAction,
    clearSurveyScoresAction,
    clearQuestionScoresAction,
    fetchDigitalQuestionScoresAction,
  ]);

  useEffect(() => {
    if (!trialId) {
      return;
    }
    getSharedLinkAction('surveyV2', 'code', trialId);
  }, [trialId, getSharedLinkAction]);

  const addGeneratedCases = useCallback((generatedCases: DigitalCase[]) => {
    const promisedCases = generatedCases.map((c) => ApiClient.addDigitalCase(c));
    Promise.all(promisedCases)
      .then((newCases) => {
        newCases.forEach((c) => addCaseReduxAction(c));
        setSelectedCaseAction(newCases[0].id);
      })
      .catch((e) => showErrorNotificationAction(e.message));
  }, [addCaseReduxAction, setSelectedCaseAction, showErrorNotificationAction]);

  const saveCaseSortOrder = useCallback(async (dc: DigitalCase[]) => {
    const promiseArr = dc.map((c) => ApiClient.updateDigitalCase(c.id, c));
    try {
      const resolvedList = await Promise.all(promiseArr);
      setDigitalCasesAction(resolvedList);
    } catch (e) {
      showErrorNotificationAction(JSON.stringify(e));
    }
  }, [setDigitalCasesAction, showErrorNotificationAction]);

  const saveQuestionSortOrder = useCallback(async (dq: DigitalQuestion[]) => {
    const promiseArr = dq.map((q) => ApiClient.updateDigitalQuestion(q));
    try {
      const resolvedList = await Promise.all(promiseArr);
      const newQuestionMap = resolvedList.reduce((acc, current) => {
        acc[current.id] = current;
        return acc;
      }, { ...questionsById });
      setDigitalQuestionsAction(Object.values(newQuestionMap));
    } catch (e) {
      showErrorNotificationAction(JSON.stringify(e));
    }
  }, [questionsById, setDigitalQuestionsAction, showErrorNotificationAction]);

  const applyEvaluation = async () => {
    const { id: tId } = trial || {};
    if (!tId) {
      return;
    }
    setLoadingAction(true);
    try {
      const res = await ApiClient.evaluateAll(tId);
      showNotificationAction(`Success, ${res.length} processsed`);
    } catch (e) {
      showErrorNotificationAction((e as Error).message);
    } finally {
      setLoadingAction(false);
    }
  };

  const onDeleteQuestion = useCallback((digitalQuestionId: number) => {
    ApiClient.checkSubmissionExist({ digitalQuestionId })
      .then(({ count }) => {
        if (count) {
          setWarningTitle('Delete Question');
          setSelectedModel(undefined);
          setWarningMessage('Cannot delete question. A submission entry has been made on this question');
          return;
        }

        setWarningTitle('Delete Question');
        setWarningMessage('Are you sure you want to delete this question?');
        setSelectedModel({ id: digitalQuestionId, callback: deleteDigitalQuestionAction });
      });
  }, [deleteDigitalQuestionAction]);

  const onDeleteCase = useCallback((digitalCaseId: number) => {
    ApiClient.checkSubmissionExist({ digitalCaseId })
      .then(({ count }) => {
        if (count) {
          setWarningTitle('Delete Case');
          setSelectedModel(undefined);
          setWarningMessage('Cannot delete question. A submission entry has been made on this case');
          return;
        }

        setWarningTitle('Delete Case');
        setWarningMessage('Are you sure you want to delete this case?');
        setSelectedModel({ id: digitalCaseId, callback: deleteDigitalCaseAction });
      });
  }, [deleteDigitalCaseAction]);

  const doCreateSharedLink = async () => {
    if (!trialId) {
      return;
    }
    await createSharedLinkAction('surveyV2', { code: trialId.toString() });
  };

  const doDeleteSharedLink = async () => {
    if (!sharedLink || !trialId) {
      return;
    }
    await deleteSharedLinkAction(sharedLink);
  };

  const deleteGroundTruthByQuestionAndCase = useCallback((questionId: number, caseId: number) => {
    const gtAlongGroups = groundTruthsPerSection
      .filter((g) => g.digitalQuestionId === questionId && g.digitalCaseId === caseId);
    if (gtAlongGroups.length) {
      gtAlongGroups.forEach((e) => deleteDigitalGroundTruthAction(e.id));
    }
  }, [deleteDigitalGroundTruthAction, groundTruthsPerSection]);

  if (!survey) {
    return null;
  }

  const functionCall = sharedLink ? doDeleteSharedLink : doCreateSharedLink;
  const sharedLinkText = sharedLink ? `${getBaseUrl()}/pblc?token=${sharedLink}` : '';

  return (
    <SurveyOptionContext.Provider value={{ disableEdit, trialHasStarted }}>
      <Box display="flex" justifyContent="space-between" align-items="flex-end">
        <HeadingTitle title="Survey Editor" />
      </Box>
      <Box sx={{ mx: 2 }}>
        <Tabs
          aria-label="basic tabs example"
          value={tabValue}
          onChange={(event: React.SyntheticEvent, newValue: number) => {
            setTabValue(newValue);
          }}
        >
          <Tab label="Questions and Cases" />
          <Tab label="Scores" />
          <Tab label="Submission Aggregations" />
          <Tab label="Färbung Review Settings" hidden={trial?.reviewCategoryId !== 2} />
        </Tabs>
      </Box>
      {
        disableEdit && (
          <Paper
            sx={{
              mx: '1em',
              width: '84vw',
            }}
          >
            <Alert severity="warning">
              Trial has started. Survey editing will be disabled.
            </Alert>
          </Paper>
        )
      }
      <Paper
        variant="outlined"
        sx={{
          mx: '1em',
          width: '84vw',
        }}
      >
        <Box aria-label="container" sx={{ p: 2 }}>
          <Box
            aria-label="section-editor-container"
            sx={{
              maxWidth: 'lg',
              px: 6,
              my: 3,
            }}
          >
            <SubHeadingTitle title="Sections" />
            <SectionSettings
              sections={sections}
              selectedSection={selectedSection}
              setSelectedSection={setSelectedDigitalsectionAction}
              updateDigitalSection={updateDigitalSectionAction}
            />
          </Box>
          <Divider />
          <TabPanel value={tabValue} index={0}>
            <Box
              aria-label="further-section-settings"
              sx={{
                maxWidth: 'lg',
                px: 6,
                my: 3,
              }}
            >
              <SubHeadingTitle title="Reference Links" />
              <Box aria-label="reference-link-container">
                {
                  selectedSection && selectedSection.referenceLinks && (
                    <Box sx={{ m: 2, p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        <FormattedMessage id="referencelink.title" />
                      </Typography>
                      <ReferenceLinks
                        selectedSection={selectedSection}
                      />
                    </Box>
                  )
                }
              </Box>
              {
                selectedSection && (
                  <Box sx={{ mt: 1 }}>
                    <SectionReferenceLinksEditor
                      selectedSection={selectedSection}
                      updateDigitalSection={updateDigitalSectionAction}
                      disable={disableEdit}
                    />
                  </Box>
                )
              }
            </Box>
            <Divider />
            <Box
              aria-label="case-editor-container"
              sx={{
                maxWidth: 'lg',
                px: 6,
                my: 3,
              }}
            >
              <SubHeadingTitle title="Cases" />
              <Box aria-label="case-editor" sx={{ my: 2, py: 3 }}>
                {
                  selectedSection && (
                    <CaseEditor
                      cases={cases}
                      sectionId={selectedSection.id}
                      surveyId={survey.id}
                      onGenerateCases={addGeneratedCases}
                      onAddCase={addDigitalCaseAction}
                      onUpdateCase={updateDigitalCaseAction}
                      onDeleteCase={onDeleteCase}
                      onSortSave={saveCaseSortOrder}
                    />
                  )
                }
              </Box>
            </Box>
            <Divider />
            <Box
              aria-label="question-editor-container"
              sx={{
                maxWidth: 'lg',
                px: 6,
                my: 3,
              }}
            >
              <SubHeadingTitle title="Questions" />
              <Box aria-label="question-editor" sx={{ my: 2, py: 3 }}>
                {
                  selectedSection && (
                    <QuestionEditor
                      surveyId={survey?.id}
                      trialId={survey?.trialId}
                      sectionId={selectedSection.id}
                      questions={questions}
                      addQuestion={addDigitalQuestionAction}
                      updateQuestion={updateDigitalQuestionAction}
                      onSortSave={saveQuestionSortOrder}
                      deleteQuestion={onDeleteQuestion}
                      disabledQuestions={[QuestionTypeV2.ChoiceWithText]}
                      enableTNA
                    />
                  )
                }
              </Box>
            </Box>
            <Divider />
            <Box
              aria-label="context-questions"
              sx={{
                maxWidth: 'lg',
                px: 6,
                my: 3,
              }}
            >
              <SubHeadingTitle title={<FormattedMessage id="review.methodicalQuestionTitle" />} />
              <Box aria-label="question-editor" sx={{ my: 2, py: 3 }}>
                {
                  selectedSection && (
                    <QuestionEditor
                      isContextQuestion
                      surveyId={survey?.id}
                      trialId={survey?.trialId}
                      sectionId={selectedSection.id}
                      questions={contextQuestions}
                      addQuestion={addDigitalQuestionAction}
                      updateQuestion={updateDigitalQuestionAction}
                      onSortSave={saveQuestionSortOrder}
                      deleteQuestion={onDeleteQuestion}
                      removeHideInAggregationCheckbox
                    />
                  )
                }
              </Box>
            </Box>
            <Divider />
            <Box
              aria-label="Question-Case-Settings-Grid"
              sx={{
                margin: '0 auto',
                px: 6,
                my: 3,
              }}
            >
              <SubHeadingTitle title="Question Settings" />
              <Box aria-label="question-editor" sx={{ my: 2, py: 3 }}>
                <Typography variant="h6">Main Questions Settings</Typography>
                <QuestionCaseSettingsGrid
                  digitalSurveyId={survey.id}
                  selectedSectionId={selectedSection?.id}
                  digitalQuestions={questions}
                  digitalCases={cases}
                  settingsByQuestionAndCase={settingsByQuestionAndCase}
                  addQuestionCaseSetting={addQuestionCaseSettingAction}
                  updateQuestionCaseSetting={updateQuestionCaseSettingAction}
                  bulkSettingsAllCasesPerQuestion={bulkSettingsAllCasesPerQuestionAction}
                  bulkSettingsAllQuestionsPerCase={bulkSettingsAllQuestionsPerCaseAction}
                  deleteGTByQuestionAndCase={deleteGroundTruthByQuestionAndCase}
                  fetchDigitalGroundTruths={fetchDigitalGroundTruthsAction}
                />
                <Box sx={{ mt: 4 }}>
                  <ContextQuestionSettings
                    selectedSectionId={selectedSection?.id}
                    contextQuestions={contextQuestions}
                    settingsByCtxQuestionsId={contextQuestionSettingByQuestionId}
                    addQuestionCaseSetting={addQuestionCaseSettingAction}
                    updateQuestionCaseSetting={updateQuestionCaseSettingAction}
                  />
                </Box>
              </Box>
            </Box>
            <Divider />
            <Box
              aria-label="settings-container"
              sx={{
                maxWidth: 'lg',
                px: 6,
                my: 3,
              }}
            >
              <SubHeadingTitle title="Settings" />
              <Box sx={{ mt: 1 }}>
                <Button
                  variant="contained"
                  id="FinalizeSubmitButton"
                  color={sharedLink ? 'secondary' : 'primary'}
                  disabled={fetchingSharedLink}
                  onClick={(): Promise<void> => functionCall()}
                >
                  {sharedLink ? 'End Shared Link' : 'Create Shared Link'}
                </Button>
                {sharedLink && (
                  <Box
                    sx={{ display: 'inline', ml: 1 }}
                  >
                    <Chip
                      label={sharedLinkText}
                      variant="outlined"
                      color="info"
                      icon={<ContentCopy />}
                      onClick={(): void => {
                        navigator.clipboard.writeText(sharedLinkText).then(() => {
                          showNotificationAction('Link copied to clipboard');
                        });
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <Box
              aria-label="Groundtruth-editor-container"
              sx={{
                margin: '0 auto',
                px: 6,
                my: 3,
              }}
            >
              <Box display="flex">
                <SubHeadingTitle flexGrow={1} title="Ground Truth" />
                <Button
                  variant="contained"
                  onClick={applyEvaluation}
                  size="small"
                >
                  <FormattedMessage id="trialdetails.evaluate" />
                </Button>
              </Box>
              <Box aria-label="question-editor" sx={{ my: 2, py: 3 }}>
                <GroundTruthToolbar
                  surveyId={survey.id}
                  selectedSectionId={selectedSection?.id}
                  groundTruthGroups={groundTruthGroups}
                  selectedGroundTruthGroup={selectedGroundTruthGroup}
                  digitalSectionScores={digitalSectionScores}
                  addGroundTruthGroup={addDigitalGroundTruthGroupAction}
                  setSelectedGroundTruthGroup={setSelectedDigitalGroundTruthGroupAction}
                  deleteDigitalGroundTruthGroup={deleteDigitalGroundTruthGroupAction}
                />
                {
                    selectedGroundTruthGroup && (
                      <DigitalGroundTruthEditor
                        digitalQuestions={questions}
                        digitalCases={cases}
                        digitalSection={selectedSection}
                        digitalSurveyId={survey.id}
                        DGTDictionaryByQuestionAndCase={gtDictionaryByQuestionAndCase}
                        settingsByQuestionAndCase={settingsByQuestionAndCase}
                        groundTruthGroupId={selectedGroundTruthGroup.id}
                        addOrUpdateGroundTruth={addOrUpdateDigitalGroundTruthAction}
                        deleteGroundTruth={deleteDigitalGroundTruthAction}
                        showErrorNotification={showErrorNotificationAction}
                      />
                    )
                  }
              </Box>
            </Box>
            <Divider />
            <Box
              aria-label="Scores-editor-container"
              sx={{
                px: 6,
                my: 3,
              }}
            >
              <SubHeadingTitle title="Scores" />
              <Box aria-label="Scores-editor" sx={{ py: 3 }}>
                <QuestionScoreEditor
                  questions={questions}
                  questionScores={digitalQuestionScores}
                  selectedGroundTruthGroupId={selectedGroundTruthGroup?.id}
                  addOrUpdateDigitalQuestionScore={addOrUpdateDigitalQuestionScoreAction}
                />
                <Divider sx={{ my: 2 }} />
                {
                  selectedSection && (
                    <ScoreEditor
                      key={`${selectedSection.id}-${selectedGroundTruthGroup?.id}`}
                      section={selectedSection}
                      gtGroup={selectedGroundTruthGroup}
                      digitalSectionScores={digitalSectionScores}
                      digitalSurveyScores={digitalSurveyScores}
                      addOrUpdateDigitalSectionScore={addOrUpdateDigitalSectionScoreAction}
                      addOrUpdateDigitalSurveyScore={addOrUpdateDigitalSurveyScoreAction}
                    />
                  )
                }
              </Box>
            </Box>

          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            {trial && (
              <AggregatedTabPanel
                selectedSection={selectedSection}
                digitalQuestions={questions}
                groundTruthGroups={groundTruthGroups}
                digitalCases={cases}
                selectedGroundTruthGroup={selectedGroundTruthGroup}
                setSelectedGroundTruthGroup={setSelectedDigitalGroundTruthGroupAction}
                aggregatedSubmission={aggregatedSubmission}
                settingsDictionary={settingsDictionary}
                trialCode={trial.code}
              />
            )}
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            {
              trial?.reviewCategoryId === 2 && !!selectedSection && (
                <Box
                  aria-label="Faerbung-Review-Settings-container"
                  sx={{
                    margin: '0 auto',
                    px: 6,
                    my: 3,
                  }}
                >
                  <SubHeadingTitle title="Färbung Review Settings" />
                  <Box aria-label="Faerbung-review-settings" sx={{ my: 2, py: 3 }}>
                    <FaerbungReviewSettings
                      surveyId={survey.id}
                      sections={sections}
                      selectedSection={selectedSection}
                    />
                  </Box>
                </Box>
              )
            }
          </TabPanel>
        </Box>
        <Prompt
          display={!!warningTitle}
          alert={!selectedModel?.id}
          action={(confirm: boolean): void => {
            setWarningTitle('');
            if (!confirm || !selectedModel?.id) {
              return;
            }
            selectedModel?.callback(selectedModel?.id);
            setSelectedModel(undefined);
          }}
          title={warningTitle}
          version={2}
        >
          <Typography>{warningMessage}</Typography>
        </Prompt>
      </Paper>
    </SurveyOptionContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  survey: state.SurveyData.survey,
  sections: selectDigitalSections(state),
  sectionsById: state.SurveyData.digitalSections.byId,
  selectedSection: selectSelectedSection(state),
  selectedCase: selectSelectedCase(state),
  cases: selectDigitalCases(state),
  casesById: state.SurveyData.digitalCases.byId,
  questions: selectDigitalQuestionsBySection(state),
  contextQuestions: selectContextQuestionBySection(state),
  questionsById: state.SurveyData.digitalQuestions.byId,
  gtDictionaryByQuestionAndCase: selectDigitalGTDictionaryByQuestionAndCase(
    state,
    state.SurveyData.digitalSections.selectedSection,
  ),
  groundTruthsPerSectionAndGroup: selectGroundTruthsPerSectionAndGroup(state),
  groundTruthsPerSection: selectGroundTruthsPerSection(state),
  settingsByQuestionAndCase: selectSettingsDictionaryByQuestionAndCase(state),
  contextQuestionSettingByQuestionId: selectContextQuestionSettingsByQuestionId(state),
  sharedLink: state.publicSharedLink.sharedLink,
  fetchingSharedLink: state.publicSharedLink.fetchingSharedLink,
  groundTruthGroups: selectDigitalGTGroups(state),
  selectedGroundTruthGroup: selectSelectedGroundTruthGroup(state),
  digitalSectionScores: selectDigitalSectionScores(state),
  digitalSurveyScores: selectDigitalSurveyScores(state),
  digitalQuestionScores: selectDigitalQuestionScores(state),
  settingsDictionary: selectSettingsDictionaryByQuestionAndCase(state),
  isEditorPrivileged: selectIsEditorPrivileged(state),
});

const dispatchProps = {
  resetDigitalSurvey,
  addCase,
  addDigitalCase,
  addDigitalGroundTruthGroup,
  addDigitalQuestion,
  addOrUpdateDigitalGroundTruth,
  addOrUpdateDigitalSectionScore,
  addOrUpdateDigitalSurveyScore,
  addQuestionCaseSetting,
  bulkSettingsAllCasesPerQuestion,
  bulkSettingsAllQuestionsPerCase,
  clearDigitalSectionScores,
  clearDigitalSurveyScores,
  createSharedLink,
  deleteDigitalCase,
  deleteDigitalGroundTruth,
  deleteDigitalGroundTruthGroup,
  deleteDigitalQuestion,
  deleteSharedLink,
  fetchDigitalGroundTruthGroups,
  fetchDigitalGroundTruths,
  fetchDigitalSectionScores,
  fetchDigitalSurveyScores,
  fetchFullDigitalSurvey,
  getSharedLink,
  setDigitalCases,
  setDigitalQuestions,
  setSelectedCase,
  setSelectedDigitalGroundTruthGroup,
  setSelectedDigitalsection,
  showErrorNotification,
  showNotification,
  updateDigitalCase,
  updateDigitalQuestion,
  updateDigitalSection,
  updateDigitalSurvey,
  updateQuestionCaseSetting,
  fetchDigitalQuestionScores,
  clearDigitalQuestionScores,
  addOrUpdateDigitalQuestionScore,
  setLoading,
};
const connector = connect(mapStateToProps, dispatchProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(SurveyEditor);
